import { useRef, useState, type ReactNode } from 'react';
import { TabelaCartoes } from '@/components/common/TabelaCartoes';
import { ListaMobile, ItemLista, IconeQuadrado } from '@/components/common/ListaMobile';
import { Plus, Pencil, Trash2, Loader2, X, ImageOff, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CATEGORIAS } from '@/api/endpoints/orcamentos';
import type { Produto, ProdutoInput } from '@/api/endpoints/orcamentos';
import {
  useProdutos,
  useCreateProduto,
  useUpdateProduto,
  useDeleteProduto,
} from '@/hooks/useOrcamentos';
import { brl } from './calc';

const card = 'rounded-xl border border-border bg-bg-elevated p-4';
const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';
const inputCls =
  'w-full rounded-lg border border-border bg-bg-main px-3 py-2 text-sm text-text-title outline-none focus:border-primary';

const EMPTY: ProdutoInput = {
  categoria: 'leitor',
  nome: '',
  modelo: '',
  origem: 'alibaba',
  fornecedor: '',
  moedaCusto: 'USD',
  custoUsd: 0,
  custoBrl: 0,
  pesoG: 0,
  comprimentoCm: 0,
  larguraCm: 0,
  alturaCm: 0,
  specs: [],
  imagemUrl: '',
  ativo: true,
  ordem: 0,
};

const nf = (v: string) => (v === '' ? 0 : Number(v));

const categoriaLabel = (valor: string) => CATEGORIAS.find((c) => c.value === valor)?.label ?? valor;

const custoLabel = (p: Produto) => (p.moedaCusto === 'USD' ? `US$ ${p.custoUsd}` : brl(p.custoBrl));

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export function CatalogoTab() {
  const produtosQ = useProdutos();
  const create = useCreateProduto();
  const update = useUpdateProduto();
  const remove = useDeleteProduto();

  const [editing, setEditing] = useState<Produto | null>(null);
  const [form, setForm] = useState<ProdutoInput | null>(null);
  // O formulário fica no topo da aba; no celular, abrir a edição pela lista
  // rola até ele para não ficar fora da tela.
  const formRef = useRef<HTMLDivElement>(null);

  const abrirNovo = () => {
    setEditing(null);
    setForm({ ...EMPTY });
  };
  const abrirEdicao = (p: Produto) => {
    setEditing(p);
    const { id: _id, ...rest } = p;
    void _id;
    setForm({ ...rest });
  };
  const fechar = () => {
    setForm(null);
    setEditing(null);
  };

  const salvar = () => {
    if (!form || !form.nome.trim()) return;
    if (editing) {
      update.mutate({ id: editing.id, data: form }, { onSuccess: fechar });
    } else {
      create.mutate(form, { onSuccess: fechar });
    }
  };

  const editarPelaLista = (p: Produto) => {
    abrirEdicao(p);
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  };

  const excluir = (p: Produto) => {
    if (confirm(`Remover "${p.nome}" do catálogo?`)) remove.mutate(p.id);
  };

  const produtos = produtosQ.data ?? [];
  const saving = create.isPending || update.isPending;
  // Carregando / vazio: a mesma mensagem nas duas versões da lista.
  const aviso = produtosQ.isLoading
    ? 'Carregando…'
    : produtos.length === 0
      ? 'Nenhum produto cadastrado. Clique em “Adicionar produto”.'
      : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">
          Produtos que entram nos orçamentos. O custo em US$ atualiza pelo câmbio ao vivo na hora do
          cálculo.
        </p>
        {!form && (
          <Button size="sm" onClick={abrirNovo}>
            <Plus size={16} /> Adicionar produto
          </Button>
        )}
      </div>

      {/* Formulário de criação/edição */}
      {form && (
        <div ref={formRef} className={cn(card, 'scroll-mt-4')}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text-title">
              {editing ? `Editar: ${editing.nome}` : 'Novo produto'}
            </h3>
            <button className="text-text-secondary hover:text-error" onClick={fechar}>
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Categoria">
              <select
                className={inputCls}
                value={form.categoria}
                onChange={(e) =>
                  setForm({ ...form, categoria: e.target.value as ProdutoInput['categoria'] })
                }
              >
                {CATEGORIAS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Nome">
              <input
                className={inputCls}
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </Field>
            <Field label="Modelo">
              <input
                className={inputCls}
                value={form.modelo}
                onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              />
            </Field>
            <Field label="Origem">
              <select
                className={inputCls}
                value={form.origem}
                onChange={(e) =>
                  setForm({ ...form, origem: e.target.value as ProdutoInput['origem'] })
                }
              >
                <option value="alibaba">Alibaba (importado)</option>
                <option value="proprio">Próprio</option>
                <option value="nacional">Nacional</option>
              </select>
            </Field>
            <Field label="Moeda do custo">
              <select
                className={inputCls}
                value={form.moedaCusto}
                onChange={(e) =>
                  setForm({ ...form, moedaCusto: e.target.value as ProdutoInput['moedaCusto'] })
                }
              >
                <option value="USD">USD (sofre imposto de importação)</option>
                <option value="BRL">BRL (nacional, sem imposto)</option>
              </select>
            </Field>
            <Field label="Fornecedor (ref. interna)">
              <input
                className={inputCls}
                value={form.fornecedor}
                onChange={(e) => setForm({ ...form, fornecedor: e.target.value })}
              />
            </Field>
            <Field label="Custo US$">
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={form.custoUsd}
                onChange={(e) => setForm({ ...form, custoUsd: nf(e.target.value) })}
              />
            </Field>
            <Field label="Custo R$ (se nacional)">
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={form.custoBrl}
                onChange={(e) => setForm({ ...form, custoBrl: nf(e.target.value) })}
              />
            </Field>
            <Field label="Peso (g)">
              <input
                type="number"
                className={inputCls}
                value={form.pesoG}
                onChange={(e) => setForm({ ...form, pesoG: nf(e.target.value) })}
              />
            </Field>
            <Field label="Comprimento (cm)">
              <input
                type="number"
                step="0.1"
                className={inputCls}
                value={form.comprimentoCm}
                onChange={(e) => setForm({ ...form, comprimentoCm: nf(e.target.value) })}
              />
            </Field>
            <Field label="Largura (cm)">
              <input
                type="number"
                step="0.1"
                className={inputCls}
                value={form.larguraCm}
                onChange={(e) => setForm({ ...form, larguraCm: nf(e.target.value) })}
              />
            </Field>
            <Field label="Altura (cm)">
              <input
                type="number"
                step="0.1"
                className={inputCls}
                value={form.alturaCm}
                onChange={(e) => setForm({ ...form, alturaCm: nf(e.target.value) })}
              />
            </Field>
            <Field label="Specs (uma por linha)">
              <textarea
                className={inputCls}
                rows={2}
                value={form.specs.join('\n')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    specs: e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Field>
            <div className="sm:col-span-2 lg:col-span-2">
              <label className={labelCls}>URL da imagem (exibida no seletor)</label>
              <div className="flex items-center gap-3">
                <input
                  className={inputCls}
                  value={form.imagemUrl}
                  onChange={(e) => setForm({ ...form, imagemUrl: e.target.value })}
                  placeholder="https://..."
                />
                {form.imagemUrl ? (
                  <img
                    src={form.imagemUrl}
                    alt=""
                    className="size-11 shrink-0 rounded-lg border border-border object-cover"
                  />
                ) : null}
              </div>
            </div>
            <Field label="Ordem">
              <input
                type="number"
                className={inputCls}
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: nf(e.target.value) })}
              />
            </Field>
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-text-title">
              <input
                type="checkbox"
                checked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              />
              Ativo
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={fechar}>
              Cancelar
            </Button>
            <Button size="sm" onClick={salvar} disabled={saving || !form.nome.trim()}>
              {saving && <Loader2 size={16} className="animate-spin" />}
              {editing ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      )}

      {/* Celular e tablet: tocar na linha abre a edição (mesma ação do lápis) */}
      <ListaMobile className="lg:hidden">
        {aviso ? (
          <li className="px-4 py-6 text-center text-sm text-text-secondary">{aviso}</li>
        ) : (
          produtos.map((p) => (
            <ItemLista
              key={p.id}
              onClick={() => editarPelaLista(p)}
              inicio={
                p.imagemUrl ? (
                  <img
                    src={p.imagemUrl}
                    alt=""
                    className="size-10 rounded-xl border border-border object-cover"
                  />
                ) : (
                  <IconeQuadrado>
                    <Package size={18} />
                  </IconeQuadrado>
                )
              }
              titulo={p.nome}
              apoio={[p.modelo, categoriaLabel(p.categoria), `${p.pesoG} g`]
                .filter(Boolean)
                .join(' · ')}
              fim={
                <>
                  <span className="text-[15px] font-bold tabular-nums text-text-title">
                    {custoLabel(p)}
                  </span>
                  <span
                    className={cn(
                      'flex items-center gap-1 text-[11px]',
                      p.ativo ? 'text-tertiary' : 'text-text-hint'
                    )}
                  >
                    <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
                    {p.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </>
              }
              acao={
                <button
                  type="button"
                  onClick={() => excluir(p)}
                  aria-label={`Remover ${p.nome} do catálogo`}
                  className="flex size-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:text-error active:bg-muted/60"
                >
                  <Trash2 size={17} />
                </button>
              }
            />
          ))
        )}
      </ListaMobile>

      {/* Tabela */}
      <div className={card + ' hidden overflow-x-auto lg:block'}>
        {aviso ? (
          <p className="py-6 text-center text-sm text-text-secondary">{aviso}</p>
        ) : (
          <TabelaCartoes className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-secondary">
                <th className="py-2">Produto</th>
                <th className="py-2">Categoria</th>
                <th className="py-2 text-right">Custo</th>
                <th className="py-2 text-right">Peso</th>
                <th className="py-2">Status</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {produtos.map((p) => (
                <tr key={p.id} className="border-b border-border/60">
                  <td className="py-2 text-text-title">
                    <div className="flex items-center gap-2.5">
                      {p.imagemUrl ? (
                        <img
                          src={p.imagemUrl}
                          alt=""
                          className="size-9 shrink-0 rounded-md border border-border object-cover"
                        />
                      ) : (
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-bg-main text-text-secondary">
                          <ImageOff size={14} />
                        </span>
                      )}
                      <span>
                        {p.nome}
                        {p.modelo ? (
                          <span className="text-text-secondary"> ({p.modelo})</span>
                        ) : null}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 capitalize text-text-secondary">{p.categoria}</td>
                  <td className="py-2 text-right text-text-secondary">{custoLabel(p)}</td>
                  <td className="py-2 text-right text-text-secondary">{p.pesoG} g</td>
                  <td className="py-2">
                    <span
                      className={
                        p.ativo
                          ? 'rounded-md bg-tertiary/15 px-2 py-0.5 text-xs font-medium text-tertiary'
                          : 'rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary'
                      }
                    >
                      {p.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-text-secondary hover:text-primary"
                        onClick={() => abrirEdicao(p)}
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="text-text-secondary hover:text-error"
                        onClick={() => excluir(p)}
                        title="Remover"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaCartoes>
        )}
      </div>
    </div>
  );
}
